import { deserializeSuperjson } from '@lib/superjson';
import { QueryDefinition } from '@reduxjs/toolkit/query';

import { api, type ApiTagTypes } from '../api';
import { BaseQuery, EndpointBuilderType, EndpointFunctionType } from './types';

type QueryReturnType<Input, Result> = QueryDefinition<
  Input,
  BaseQuery,
  ApiTagTypes,
  Result,
  'api'
>;

function createQuery<Input, Result>(
  builder: EndpointBuilderType,
  query: EndpointFunctionType<Input>,
  keepUnusedDataFor?: number,
): QueryReturnType<Input, Result> {
  return builder.query<Result, Input>({
    keepUnusedDataFor,
    query,
    transformResponse: deserializeSuperjson,
  });
}

/**
 * Creates and injects a new query endpoint into the api slice.
 *
 * @param name The name of the query hook and endpoint.
 * @param query The query function that will be used to generate the query hook and endpoint.
 * @param keepUnusedDataFor The amount of time in seconds to keep the query data in the cache
 *    after it has zero remaining subscribers.
 * @returns A tuple containing the endpoint and the useQuery hook.
 */
export function injectQuery<Input, Result>(
  name: string,
  query: EndpointFunctionType<Input>,
  keepUnusedDataFor?: number,
) {
  const injectedApi = api.injectEndpoints({
    endpoints: (builder) => ({
      [name]: createQuery<Input, Result>(builder, query, keepUnusedDataFor),
    }),
    overrideExisting: false,
  });

  const endpoint = injectedApi.endpoints[name];
  const useQuery = endpoint.useQuery;

  return [endpoint, useQuery] as const;
}
